//  src/config/firebase.ts
import { initializeApp } from 'firebase/app';
import {
  getFirestore,
  initializeFirestore,
  connectFirestoreEmulator,
  persistentLocalCache,
  persistentMultipleTabManager,
  Firestore
} from 'firebase/firestore';
import {
  getAuth,
  connectAuthEmulator,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence
} from 'firebase/auth';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getAnalytics, isSupported, Analytics } from 'firebase/analytics';
import { getMessaging, isSupported as isMessagingSupported, Messaging } from 'firebase/messaging';
import { logDev, logInfo, logError } from '../utils/logger';


// 判斷是否使用模擬器
// ⚠️ 注意：只在明確設定 REACT_APP_USE_EMULATOR=true 時才使用模擬器
// Firebase Cloud Messaging 沒有模擬器支援，必須使用真實的 Firebase 配置
const useEmulator = process.env.REACT_APP_USE_EMULATOR === 'true';

// 根據環境選擇配置
// ⚠️ 重要：FCM 沒有模擬器支援，即使在開發環境也必須使用真實配置
// 因此所有環境都使用真實的 Firebase 配置，只是連線目標（Firestore/Auth/Functions）會指向模擬器
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL
};

// 初始化 Firebase App 
const app = initializeApp(firebaseConfig);

// 根據環境初始化 Firestore
let db: Firestore;
if (useEmulator) {
  // 模擬器環境：不使用離線持久化
  db = getFirestore(app);
  logInfo('Firebase', `模擬器環境初始化 (NODE_ENV: ${process.env.NODE_ENV})`);
} else {
  // 生產環境：使用新的 cache API 啟用離線持久化
  db = initializeFirestore(app, {
    localCache: persistentLocalCache({
      tabManager: persistentMultipleTabManager()
    })
  });
  logInfo('Firebase', 'Firestore 離線持久化已啟用（使用新版 API）');
}

// 初始化其他服務
const auth = getAuth(app);
const functions = getFunctions(app, 'us-central1');

// 初始化 Analytics（只在生產環境且瀏覽器支援時）
let analytics: Analytics | null = null;
if (!useEmulator) {
  // 非同步檢查是否支援 Analytics
  isSupported().then((supported) => {
    if (supported && firebaseConfig.measurementId) {
      analytics = getAnalytics(app);
      logInfo('Firebase', `Analytics 已初始化: ${firebaseConfig.measurementId}`);
    } else {
      logDev('Firebase', 'Analytics 不支援或未設定 measurementId');
    }
  }).catch((error) => {
    logError('Firebase', 'Analytics 初始化檢查失敗', error);
  });
} else {
  logDev('Firebase', '模擬器環境，Analytics 已停用');
}

// 初始化 Messaging
// ⚠️ 注意:Firebase Cloud Messaging 沒有模擬器支援
// 即使在開發環境使用模擬器,FCM 仍然需要連接到真實的 Firebase 服務
let messaging: Messaging | null = null;
let messagingPromise: Promise<Messaging | null>;

// 始終嘗試初始化 Messaging (不受 useEmulator 影響)
messagingPromise = isMessagingSupported().then((supported) => {
  if (supported) {
    messaging = getMessaging(app);
    logInfo('Firebase', 'Cloud Messaging 已初始化');
    return messaging;
  } else {
    logDev('Firebase', 'Cloud Messaging 不支援');
    return null;
  }
}).catch((error) => {
  logError('Firebase', 'Cloud Messaging 初始化檢查失敗', error);
  return null;
});

// 模擬器端口配置
const EMULATOR_PORTS = {
  firestore: 8080,
  auth: 9099,
  functions: 5001
};

// 在模擬器環境連接模擬器
if (useEmulator) {
  try {
    // 使用配置的 IP 地址，支援區網內設備訪問（如手機測試）
    const host = process.env.REACT_APP_DEV_MACHINE_IP || 'localhost';

    // 連接模擬器
    connectFirestoreEmulator(db, host, EMULATOR_PORTS.firestore);
    connectAuthEmulator(auth, `http://${host}:${EMULATOR_PORTS.auth}`, { disableWarnings: true });
    connectFunctionsEmulator(functions, host, EMULATOR_PORTS.functions);

    logInfo('Firebase', `已連接到模擬器 (${host}:${EMULATOR_PORTS.firestore}, ${host}:${EMULATOR_PORTS.auth}, ${host}:${EMULATOR_PORTS.functions})`);
  } catch (error) {
    logError('Firebase', '連接模擬器失敗', error);
  }
}

// Auth 持久化設置（只在生產環境）
if (!useEmulator) {
  setPersistence(auth, browserLocalPersistence).catch(error => {
    logError('Firebase', 'Auth 持久化設置失敗', error);
  });
}

// 導出 Firebase 服務
export {
  app,
  db,
  auth,
  functions,
  analytics,
  messaging,
  messagingPromise,
  useEmulator as isUsingEmulator
};